import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChipGroup, Field, SectionLabel, CheckRow } from "@/components/site/FormBits";
import { Spinner } from "@/components/site/ProtectedRoute";
import { useAuth } from "@/context/AuthContext";
import { AddOnPicker } from "@/components/site/AddOnPicker";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { APP_STAGES, COURSE_FORMATS, APP_SKILLS, APP_COLLAB, PRIVACY_NOTE } from "@/lib/constants";

export default function ApplyPage() {
  useSeo({ title: "Apply — Become a Digital Nomad for Beginners", path: "/apply", noindex: true });
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/application").then(({ data }) => {
      setState(data);
      const lead = data.lead || {};
      const prev = data.application || {};
      setF({
        name: prev.name || user.name || lead.name || "",
        stage: prev.stage || "",
        format: prev.format || "",
        goals: prev.goals || lead.goals || "",
        skills: prev.skills || [],
        existing_work: prev.existing_work || [lead.offer, lead.existing_project].filter(Boolean).join("\n"),
        collaboration: prev.collaboration || [],
        whatsapp: prev.whatsapp || lead.whatsapp || "",
        whatsapp_consent: prev.whatsapp_consent ?? lead.whatsapp_consent ?? false,
        retreat_optin: prev.retreat_optin ?? lead.retreat_optin ?? false,
        add_ons: prev.add_ons || lead.add_ons || [],
        add_ons_other: prev.add_ons_other || lead.add_ons_other || "",
      });
    });
  }, [user]);

  if (user.role === "host") return <Navigate to="/host" replace />;
  if (!state || !f) return <Spinner />;
  if (state.application && ["pending", "approved"].includes(state.application.status)) return <Navigate to="/dashboard" replace />;

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.stage) return toast.error("Please choose your current stage");
    if (!f.format) return toast.error("Please choose how you'd like to take the course");
    setBusy(true);
    try {
      await api.post("/me/application", f);
      toast.success("Application submitted — status: Pending");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
      <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">COURSE APPLICATION</p>
      <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2A4A]">Apply: Become a Digital Nomad for Beginners</h1>
      <p className="mt-3 text-slate-600">Your application is saved as <strong>Pending</strong> until Mr. Greg reviews it. Lessons unlock after approval.</p>
      <div className="mt-6 flex gap-3 rounded-2xl bg-[#F5ECD7] p-4 text-sm text-slate-700" data-testid="apply-privacy-note">
        <ShieldAlert className="h-5 w-5 text-[#F08A3C] shrink-0" /> {PRIVACY_NOTE}
      </div>
      <form onSubmit={submit} className="mt-10 bg-white rounded-3xl border border-[#0B2A4A]/10 p-6 sm:p-10 flex flex-col gap-8" data-testid="application-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name" htmlFor="app-name">
            <Input id="app-name" required value={f.name} onChange={set("name")} data-testid="application-name-input" />
          </Field>
          <Field label="Account email" htmlFor="app-email">
            <Input id="app-email" value={user.email} disabled data-testid="application-email-input" />
          </Field>
        </div>
        <Field label="Current stage">
          <ChipGroup options={APP_STAGES} value={f.stage} onChange={set("stage")} multi={false} testId="application-stage" />
        </Field>
        <Field label="How would you like to take the course?">
          <ChipGroup options={COURSE_FORMATS} value={f.format} onChange={set("format")} multi={false} testId="application-format" />
        </Field>
        <Field label="Goals for the course" htmlFor="app-goals">
          <Textarea id="app-goals" required rows={4} value={f.goals} onChange={set("goals")} data-testid="application-goals-input" />
        </Field>
        <Field label="Skills and interests">
          <ChipGroup options={APP_SKILLS} value={f.skills} onChange={set("skills")} testId="application-skills" />
        </Field>
        <Field label="Existing work, experience, or offer (optional)" htmlFor="app-work">
          <Textarea id="app-work" rows={3} value={f.existing_work} onChange={set("existing_work")} data-testid="application-existing-work-input" />
        </Field>
        <Field label="Collaboration interests">
          <ChipGroup options={APP_COLLAB} value={f.collaboration} onChange={set("collaboration")} testId="application-collab" />
        </Field>
        <AddOnPicker value={f.add_ons} other={f.add_ons_other} onChange={set("add_ons")} onOtherChange={set("add_ons_other")} testId="application-addons" />
        <div className="flex flex-col gap-4 border-t border-[#0B2A4A]/10 pt-6">
          <SectionLabel>Contact & consent</SectionLabel>
          <Field label="WhatsApp number (optional)" htmlFor="app-wa" hint="Include country code">
            <Input id="app-wa" value={f.whatsapp} onChange={set("whatsapp")} data-testid="application-whatsapp-input" />
          </Field>
          <CheckRow checked={f.whatsapp_consent} onChange={set("whatsapp_consent")} testId="application-whatsapp-consent">
            I consent to Mr. Greg contacting me on WhatsApp.
          </CheckRow>
          <CheckRow checked={f.retreat_optin} onChange={set("retreat_optin")} testId="application-retreat-optin">
            Send me information about future retreats (optional).
          </CheckRow>
        </div>
        <button disabled={busy} className="self-start rounded-xl bg-[#F08A3C] text-white px-7 py-3.5 font-semibold shadow-md hover:bg-[#D96F28] disabled:opacity-60" data-testid="application-submit-btn">
          {busy ? "Submitting..." : "Submit application"}
        </button>
      </form>
    </section>
  );
}
